import { templatePhasrBehavioralProfilesComponent } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import {
  BehavioralProfileTypes,
  GetDepartmentIdentitiesRecords,
  GetDepartmentsRecords,
  GetUserResourcesRecord,
  LOAD_LIMIT,
  PhasrBehavioralProfilesComponentSchema,
  RequestPayload,
} from '../types';
import { BehaviorProfileListItem, BehaviorProfilesOutput } from '../../phasrRestrictedProfilesSelector/types';
import { PhasrBehavioralProfilesComponent } from '../component/PhasrBehavioralProfilesComponent';
import { PhasrBehavioralProfilesComponentRepository } from '../repository/PhasrBehavioralProfilesComponentRepository';
import { PhasrBehavioralProfilesComponentView } from '../view/PhasrBehavioralProfilesComponentView';
import { translateFn } from '../../../utils/translator.helper';
import { isEmpty } from 'lodash';
import { Collections, PhasrApplyActionPanelEvents } from '../../../phasrApplyActionPanel/types';

export class PhasrBehavioralProfilesComponentController extends BaseWebComponentController {
  public component: PhasrBehavioralProfilesComponent;
  public dataRepository: PhasrBehavioralProfilesComponentRepository;
  public view: PhasrBehavioralProfilesComponentView;
  public viewConstructor: typeof PhasrBehavioralProfilesComponentView;
  public dataRepositoryConstructor: typeof PhasrBehavioralProfilesComponentRepository;
  public gridData: PhasrBehavioralProfilesComponentSchema;
  public departments: GetDepartmentsRecords[] = [];
  public users: GetDepartmentIdentitiesRecords[] = [];
  public profiles: GetUserResourcesRecord[] = [];
  private previousSelectedProfileType: BehavioralProfileTypes = BehavioralProfileTypes.AUTOPILOT;

  constructor(component: PhasrBehavioralProfilesComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PhasrBehavioralProfilesComponentView,
      viewTemplate: templatePhasrBehavioralProfilesComponent,
      dataRepositoryConstructor: PhasrBehavioralProfilesComponentRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$behavioralProfiles,
        event: 'departments-select',
        callback: e => this.onDepartmentSelect(e.detail.id),
      },
      {
        element: this.view.dom.$behavioralProfiles,
        event: 'users-select',
        callback: e => this.loadProfilesForUsers(e.detail.id),
      },
      {
        element: this.view.dom.$searchUsers,
        event: 'selectionChange',
        callback: this.onSearchUsersDropdownChanged,
      },
      {
        element: this.view.dom.$profileTypeSelector,
        event: 'selectionChange',
        callback: this.onProfileTypeDropdownChanged,
      },
    ];
  }

  public async getDataForView(): Promise<PhasrBehavioralProfilesComponentSchema> {
    this.gridData = this.component.getGridData();
    const departmentsResponse = await this.dataRepository.getDepartments(
      this.departmentsPayload(1, this.gridData.behavioralProfileType)
    );
    this.departments = departmentsResponse.records;

    return {
      departments: this.departments,
      collection: this.component.collection,
      ...this.gridData,
    };
  }

  async loadMoreUsers(departmentId: string): Promise<BehaviorProfileListItem[]> {
    const profilesView = this.view.dom.$behavioralProfiles;
    if (!profilesView.usersList.hasMoreItems || profilesView.searchMode) {
      return [];
    }

    const page = Math.floor(this.users.length / LOAD_LIMIT) + 1;
    const behavioralProfileType = this.component.getGridData().behavioralProfileType;
    const response = await this.dataRepository.getDepartmentIdentities(
      this.departmentIdentitiesPayload(departmentId, page, behavioralProfileType)
    );

    if (response.totalRecords <= this.users.length) {
      return [];
    }

    this.users = [...this.users, ...response.records];

    const result = Array.from(Object.values(response.records)).map(user => ({
      id: user.behavioralProfileIdentity,
      name: user.behavioralProfileIdentityName,
      status: user.status,
      parentId: departmentId,
      totalProfiles: user.totalProfiles,
      totalRestrictedProfiles: user.restrictedProfiles,
    }));

    profilesView.users = [...profilesView.users, ...result];

    return result;
  }

  loadMoreDepartments() {}

  loadMoreDevices() {}

  private onSearchUsersDropdownChanged(e: CustomEvent) {
    const user = e.detail;
    if (user.selectedValue) {
      this.view.dom.$behavioralProfiles.searchMode = true;
      this.populateDepartmentsOnSearchUser(user);
      this.populateUsersOnSearch(user);
      this.view.dom.$behavioralProfiles.profiles = [];
      this.view.dom.$behavioralProfiles.requestUpdate();
    } else {
      this.view.dom.$behavioralProfiles.searchMode = false;
      this.resetView();
    }
  }

  private async onProfileTypeDropdownChanged(e: CustomEvent) {
    if (!e.detail.selectedValue) {
      return;
    }
    this.view.setInfoBox(e.detail.selectedValue);
    this.view.dom.$behavioralProfiles.disabled = e.detail.selectedValue === BehavioralProfileTypes.AUTOPILOT;
    this.component.dispatchEvent(
      new CustomEvent(PhasrApplyActionPanelEvents.BEHAVIORAL_PROFILE_TYPE_CHANGED, {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
    if (e.detail.selectedValue === this.previousSelectedProfileType) {
      return;
    }
    this.previousSelectedProfileType = e.detail.selectedValue;

    if (this.view.dom.$behavioralProfiles.departmentsList) {
      this.view.dom.$behavioralProfiles.departmentsList.isLoading = true;

      const gridData = this.component.getGridData();
      gridData.behavioralProfileType = e.detail.selectedValue;
      this.component.setGridData(gridData);
      this.view.dom.$behavioralProfiles.departments = [];
      this.view.dom.$behavioralProfiles.users = [];
      this.view.dom.$behavioralProfiles.profiles = [];
      this.view.dom.$behavioralProfiles.resetInternalState();
      this.view.dom.$behavioralProfiles.render();
      const data = await this.getDataForView();

      this.view.setBehavioralProfiles(data);

      this.view.dom.$behavioralProfiles.departmentsList.isLoading = false;
      if (isEmpty(this.view.dom.$behavioralProfiles.departments)) {
        this.view.dom.$behavioralProfiles.departmentsList.emptyStateMessage = translateFn('No data');
      }

      this.view.setUsersDropdown(data);
    }
  }

  private resetView() {
    this.view.dom.$behavioralProfiles.departments = this.view.getDepartmentsForUi(this.departments);
    this.view.dom.$behavioralProfiles.users = [];
    this.view.dom.$behavioralProfiles.profiles = [];
    if (this.view.dom.$behavioralProfiles.usersList) {
      this.view.dom.$behavioralProfiles.usersList.activeCollectionId = '';
    }
    this.view.dom.$behavioralProfiles.render();
  }

  private populateDepartmentsOnSearchUser(user: any) {
    if (
      this.departments.filter(dep => dep.behavioralProfileDepartment === user.extraDetails.department.id).length === 0
    ) {
      this.departments.push({
        behavioralProfileDepartment: user.extraDetails.department.id,
        behavioralProfileDepartmentName: user.extraDetails.department.name,
        status: user.extraDetails.department.status,
        totalProfiles: user.extraDetails.department.totalItems,
        restrictedProfiles: user.extraDetails.department.totalRestrictedItems,
      });
    }

    this.view.dom.$behavioralProfiles.departments = [
      {
        id: user.extraDetails.department.id,
        name: user.extraDetails.department.name,
        status: user.extraDetails.department.status,
      },
    ];
  }

  private populateUsersOnSearch(user: any) {
    const profilesView = this.view.dom.$behavioralProfiles;

    if (this.users.filter(u => u.behavioralProfileIdentity === user.selectedValue).length === 0) {
      this.users.push({
        behavioralProfileIdentity: user.selectedValue,
        behavioralProfileIdentityName: user.extraDetails.user.name,
        status: user.extraDetails.user.status,
        departmentId: user.extraDetails.department.id,
        totalProfiles: user.extraDetails.user.totalItems,
        restrictedProfiles: user.extraDetails.user.totalRestrictedItems,
      });
    }

    profilesView.users = [];
    profilesView.users = [
      {
        id: user.selectedValue,
        name: user.extraDetails.user.name,
        status: user.extraDetails.user.status,
        departmentId: user.extraDetails.department.id,
        parentId: user.extraDetails.department.id,
        totalProfiles: user.extraDetails.user.totalItems,
        totalRestrictedProfiles: user.extraDetails.user.totalRestrictedItems,
      },
    ];
  }

  private departmentsPayload(page: number, behavioralProfileType: BehavioralProfileTypes): RequestPayload {
    const payload: RequestPayload = {
      page: page,
      companyId: this.gridData.companyId,
      filter: [
        {
          property: 'objectId',
          value: this.gridData.id,
        },
        {
          property: 'collection',
          value: this.component.collection,
        },
      ]
    };

    if (this.component.collection === Collections.RULES) {
      payload.filter.push({
        property: 'mode',
        value: Number(behavioralProfileType),
      });
    }

    return payload;
  }

  private departmentIdentitiesPayload(
    departmentId: string,
    page: number,
    behavioralProfileType: BehavioralProfileTypes
  ): RequestPayload {
    const payload: RequestPayload = {
      page: page,
      companyId: this.gridData.companyId,
      filter: [
        {
          property: 'objectId',
          value: this.gridData.id,
        },
        {
          property: 'collection',
          value: this.component.collection,
        },
        {
          property: 'departmentId',
          value: departmentId,
        },
      ],
      limit: LOAD_LIMIT,
    };
    if (this.component.collection === Collections.RULES) {
      payload.filter.push({
        property: 'mode',
        value: Number(behavioralProfileType),
      });
    }
    return payload;
  }

  private userResourcesPayload(
    userId: string,
    page: number,
    behavioralProfileType: BehavioralProfileTypes
  ): RequestPayload {
    const payload: RequestPayload = {
      page: page,
      companyId: this.gridData.companyId,
      filter: [
        {
          property: 'objectId',
          value: this.gridData.id,
        },
        {
          property: 'collection',
          value: this.component.collection,
        },
        {
          property: 'behavioralProfileIdentity',
          value: userId,
        },
      ]
    };
    if (this.component.collection === Collections.RULES) {
      payload.filter.push({
        property: 'mode',
        value: Number(behavioralProfileType),
      });
    }
    return payload;
  }

  /**
   * Load the users for a selected department.
   * @param departmentId
   * @private
   */
  private async loadUsersForDepartment(departmentId: string) {
    const profilesView = this.view.dom.$behavioralProfiles;
    profilesView.profiles = []; // Clear profiles list when loading new users
    profilesView.users = []; // Clear previous users
    this.users = [];

    const { users, totalUsers } = await this.getUsersForDepartment(departmentId, 1);

    profilesView.users = [...users];
    profilesView.usersList.activeCollectionId = departmentId;
    profilesView.usersList.hasMoreItems = this.users.length < totalUsers;
    profilesView.usersList.loadMoreItems = () => this.loadMoreUsers(departmentId);
    profilesView.usersList.requestUpdate();
  }

  /**
   * Get the users for a selected department and formats them in the required interface for the UI list.
   * @param departmentId
   * @param page
   * @private
   */
  private async getUsersForDepartment(
    departmentId: string,
    page: number
  ): Promise<{
    totalRestrictedProfiles: number;
    users: BehaviorProfileListItem[];
    totalUsers: number;
  }> {
    const { totalRestrictedItems } = this.getCountsInDepartment(departmentId);
    const behavioralProfileType = this.component.getGridData().behavioralProfileType;
    const response = await this.dataRepository.getDepartmentIdentities(
      this.departmentIdentitiesPayload(departmentId, page, behavioralProfileType)
    );

    this.users = [...response.records];

    return {
      totalRestrictedProfiles: totalRestrictedItems,
      totalUsers: response.totalRecords,
      users: this.users.map(user => {
        return {
          id: user.behavioralProfileIdentity,
          name: user.behavioralProfileIdentityName,
          status: user.status,
          parentId: departmentId,
          totalProfiles: user.totalProfiles,
          totalRestrictedProfiles: user.restrictedProfiles,
        };
      }),
    };
  }

  private getCountsInDepartment(id: string): {
    totalItems: number;
    totalRestrictedItems: number;
  } {
    const department = this.departments.filter(dep => dep.behavioralProfileDepartment === id)[0];
    return {
      totalItems: department.totalProfiles,
      totalRestrictedItems: department.restrictedProfiles,
    };
  }

  private async loadProfilesForUsers(userId: string) {
    this.view.dom.$behavioralProfiles.profilesList.isLoading = true;

    const profilesView = this.view.dom.$behavioralProfiles;
    profilesView.profiles = [];

    const { profiles } = await this.getProfilesForUser(userId);
    profilesView.profiles = [...profiles];
    profilesView.requestUpdate();

    this.view.dom.$behavioralProfiles.profilesList.isLoading = false;
  }

  /**
   * Get the profiles for a selected user and formats them in the required interface for the UI list.
   * @param userId
   * @private
   */
  private async getProfilesForUser(userId: string): Promise<{
    profiles: BehaviorProfileListItem[];
    totalProfiles: number;
    totalRestrictedProfiles: number;
  }> {
    const { totalItems, totalRestrictedItems } = this.getCountsInUser(userId);
    const behavioralProfileType = this.component.getGridData().behavioralProfileType;
    this.profiles = await this.dataRepository.getUserResources(
      this.userResourcesPayload(userId, 1, behavioralProfileType)
    );

    return {
      totalProfiles: totalItems,
      totalRestrictedProfiles: totalRestrictedItems,
      profiles: this.profiles.map((profile: GetUserResourcesRecord) => {
        return {
          id: profile.behavioralProfileResource,
          name: profile.behavioralProfileResourceName,
          status: profile.status,
          parentId: userId,
          departmentId: profile.departmentId,
        };
      }),
    };
  }

  private getCountsInUser(id: string): {
    totalItems: number;
    totalRestrictedItems: number;
  } {
    const user = this.users.filter(u => u.behavioralProfileIdentity === id)[0];
    return {
      totalItems: user.totalProfiles,
      totalRestrictedItems: user.restrictedProfiles,
    };
  }

  private async onDepartmentSelect(id: string) {
    this.view.dom.$behavioralProfiles.usersList.isLoading = true;
    await this.loadUsersForDepartment(id);
    this.view.dom.$behavioralProfiles.usersList.isLoading = false;
  }

  public getSelectedOutput(): BehaviorProfilesOutput {
    return this.view.dom.$behavioralProfiles.selectedOutput;
  }
}
